import React, { useState } from 'react';
import type { A2UIComponent } from './ChatComponents';
import {
  type DataModel,
  type SurfaceActionPayload,
  countBarHeight,
  distBarHeight,
  elevBar,
  gaugePosition,
  hbarPercents,
  pairBarHeights,
  resolveList,
  resolveText,
  resolveValue,
  toSurfaceAction,
} from '../utils/runReport';
import rumiAvatar from '../assets/run-report/rumi-avatar.svg';
import insightDot from '../assets/run-report/insight-dot.svg';
import gaugeKnob from '../assets/run-report/gauge-knob.svg';
import iconPaceDetail from '../assets/run-report/icon-pace-detail.svg';
import iconSplits from '../assets/run-report/icon-splits.svg';
import iconGrowth from '../assets/run-report/icon-growth.svg';
import iconLoadRecovery from '../assets/run-report/icon-load-recovery.svg';

// Run-report surface — the A2UI `analysis/v1` catalog (22 components) the server's
// RunReportCard emits. Layout and sizing follow the Figma "A2UI — 전체 분석 v3 · 카탈로그
// v0.9.1" section; colours are the `rr-*` tokens in tailwind.config.js.

// SelectItem icons are fixed per analysis id. The design only draws these four; any other
// id falls back to the emoji the server puts in `icon`.
const SELECT_ICONS: Record<string, string> = {
  pace_hr: iconPaceDetail,
  splits: iconSplits,
  growth: iconGrowth,
  load_recovery: iconLoadRecovery,
};

const BADGE_TONES: Record<string, string> = {
  mint: 'bg-rr-mint-bg text-rr-mint-text',
  lightblue: 'bg-rr-lightblue-bg text-rr-lightblue-text',
  green: 'bg-rr-green-bg text-rr-good',
  orange: 'bg-rr-orange-bg text-rr-orange-text',
  blue: 'bg-rr-pace-dim text-rr-blue-text',
};

const SUB_TONES: Record<string, string> = {
  neutral: 'text-rr-neutral',
  good: 'text-rr-good',
  warn: 'text-rr-warn',
};

const GAUGE_LABELS = ['평소보다 적어요', '딱 좋아요', '너무 많아요'] as const;
const GAUGE_ZONES = ['low', 'good', 'high'] as const;
const GAUGE_ZONE_TEXT = { low: 'text-rr-gauge-low', good: 'text-rr-good', high: 'text-rr-gauge-high' };

const Label: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <p className={`text-[11px] leading-[14px] tracking-[-0.1px] whitespace-nowrap ${className}`}>{children}</p>
);

export const RunReportSurface: React.FC<{
  surfaceId?: string;
  components: A2UIComponent[];
  dataModel?: DataModel;
  isInteractive: boolean;
  onSurfaceAction?: (action: SurfaceActionPayload, label: string) => void;
}> = React.memo(({ surfaceId, components, dataModel, isInteractive, onSurfaceAction }) => {
  const [tappedId, setTappedId] = useState<string | null>(null);
  const disabled = !isInteractive || tappedId !== null || !onSurfaceAction;
  const byId = new Map(components.map((c) => [c.id, c]));
  const text = (v: unknown) => resolveText(v, dataModel);

  const tap = (comp: A2UIComponent, label: string) => {
    if (disabled) return;
    const action = toSurfaceAction(comp.action?.event, surfaceId, comp.id);
    if (!action) {
      // Buttons open app screens (pain intake, shoe select, …) — nothing to send from here.
      console.info('[A2UI] app-only run-report action (dashboard no-op):', comp.action?.event);
      return;
    }
    setTappedId(comp.id);
    onSurfaceAction!(action, label);
  };

  const children = (comp: A2UIComponent): A2UIComponent[] =>
    ((comp.children as string[] | undefined) ?? []).map((id) => byId.get(id)).filter(Boolean) as A2UIComponent[];

  const render = (comp: A2UIComponent): React.ReactNode => {
    switch (comp.component) {
      case 'Column':
        return (
          <div key={comp.id} className="flex flex-col gap-[14px]">
            {children(comp).map(render)}
          </div>
        );

      case 'Card':
        return (
          <div
            key={comp.id}
            className="flex flex-col gap-[14px] rounded-[18px] border border-rr-card-border bg-white p-4"
          >
            {children(comp).map(render)}
          </div>
        );

      case 'SummaryCard':
        return (
          <div
            key={comp.id}
            className="flex flex-col gap-[6px] rounded-[18px] bg-rr-primary px-[18px] pb-5 pt-[18px] text-white"
          >
            {comp.eyebrow && (
              <p className="text-[11px] font-medium leading-[14px] tracking-[-0.1px] opacity-80">{comp.eyebrow}</p>
            )}
            <p className="text-[18px] font-bold leading-[26px] tracking-[-0.3px]">{text(comp.title)}</p>
            <p className="text-[13px] leading-[18px] tracking-[-0.3px] opacity-[0.92]">{comp.body}</p>
          </div>
        );

      case 'RumiText':
        return (
          <div key={comp.id} className="flex flex-col gap-[6px]">
            <div className="flex items-center gap-[6px]">
              <img src={rumiAvatar} alt="" width={22} height={22} className="block shrink-0" />
              <p className="text-[12px] font-bold leading-4 tracking-[-0.3px] text-rr-text">Rumi</p>
            </div>
            <p className="text-[14px] leading-5 tracking-[-0.3px] text-rr-text-secondary">{text(comp.text)}</p>
          </div>
        );

      case 'SelectList':
        return (
          <div
            key={comp.id}
            className="flex flex-col overflow-hidden rounded-[18px] border border-rr-card-border bg-white"
          >
            {children(comp).map((item, i) => (
              <React.Fragment key={item.id}>
                {i > 0 && <div className="h-px bg-rr-divider" />}
                {render(item)}
              </React.Fragment>
            ))}
          </div>
        );

      case 'SelectItem': {
        const icon = SELECT_ICONS[comp.action?.event?.context?.id];
        return (
          <button
            key={comp.id}
            type="button"
            disabled={disabled}
            onClick={() => tap(comp, comp.label)}
            className={`flex h-[52px] w-full items-center gap-3 bg-white px-4 text-left transition-colors enabled:hover:bg-rr-plan-bg disabled:cursor-default ${
              tappedId === comp.id ? 'bg-rr-chip-pressed' : ''
            }`}
          >
            {icon ? (
              <img src={icon} alt="" width={24} height={24} className="block shrink-0" />
            ) : (
              <span className="flex size-6 shrink-0 items-center justify-center text-[18px] leading-none">
                {comp.icon}
              </span>
            )}
            <span className="min-w-0 flex-1 text-[14px] font-medium leading-5 tracking-[-0.1px] text-rr-text">
              {comp.label}
            </span>
            <span className="shrink-0 text-[18px] leading-5 tracking-[-0.3px] text-rr-subtle">›</span>
          </button>
        );
      }

      case 'Badge':
        return (
          <div
            key={comp.id}
            className={`self-start rounded-[20px] px-2 py-1 ${BADGE_TONES[comp.tone] ?? BADGE_TONES.mint}`}
          >
            <p className="whitespace-nowrap text-[12px] font-medium leading-[18px] tracking-[-0.1px]">{comp.text}</p>
          </div>
        );

      case 'HeadGroup':
        return (
          <div key={comp.id} className="flex flex-col gap-[5px]">
            <p className="text-[16px] font-bold leading-[22px] tracking-[-0.1px] text-rr-text">{text(comp.title)}</p>
            {comp.body && (
              <p className="text-[13px] leading-[18px] tracking-[-0.3px] text-rr-text-secondary">{comp.body}</p>
            )}
          </div>
        );

      case 'Legend':
        return (
          <div key={comp.id} className="flex gap-3">
            {((comp.items as string[] | undefined) ?? []).map((label, i) => (
              <div key={label} className="flex items-center gap-[5px]">
                <div className={`size-[9px] rounded-[2px] ${i === 0 ? 'bg-rr-primary' : 'bg-rr-hr'}`} />
                <Label className="font-medium text-rr-subtle">{label}</Label>
              </div>
            ))}
          </div>
        );

      case 'PairBars': {
        const series = resolveList(comp.series, dataModel);
        const heights = pairBarHeights(series);
        return (
          <div key={comp.id} className="flex items-end justify-between">
            {series.map((s, i) => (
              <div key={i} className="flex flex-col items-center gap-[6px]">
                <div className="flex items-end gap-[3px]">
                  {(['pace', 'hr'] as const).map((k) => {
                    const h = heights[i][k];
                    const color =
                      k === 'pace'
                        ? s.current
                          ? 'bg-rr-primary'
                          : 'bg-rr-pace-dim'
                        : s.current
                          ? 'bg-rr-hr'
                          : 'bg-rr-hr-dim';
                    return h == null ? (
                      <div key={k} className="w-[11px]" />
                    ) : (
                      <div
                        key={k}
                        className={`w-[11px] rounded-b-[2px] rounded-t-[5px] ${color}`}
                        style={{ height: h }}
                      />
                    );
                  })}
                </div>
                <Label className={s.current ? 'font-bold text-rr-primary' : 'font-medium text-rr-subtle'}>
                  {s.label}
                </Label>
              </div>
            ))}
          </div>
        );
      }

      case 'DistBars':
      case 'CountBars': {
        const series = resolveList(comp.series, dataModel);
        const isDist = comp.component === 'DistBars';
        const maxKm = Math.max(0, ...series.map((s) => Number(s.distanceKm) || 0));
        return (
          <div key={comp.id} className="flex items-end gap-[9px]">
            {series.map((s, i) => {
              const value = isDist ? (Number(s.distanceKm) || 0).toFixed(1) : `${s.count ?? 0}회`;
              const height = isDist
                ? distBarHeight(Number(s.distanceKm) || 0, maxKm)
                : countBarHeight(Number(s.count) || 0);
              const tone = s.current ? 'text-rr-primary' : 'text-rr-subtle';
              return (
                <div key={i} className="flex min-w-0 flex-1 flex-col items-center gap-[5px]">
                  <Label className={`font-bold ${tone}`}>{value}</Label>
                  <div
                    className={`w-full rounded-b-[3px] rounded-t-[8px] ${s.current ? 'bg-rr-primary' : 'bg-rr-pace-dim'}`}
                    style={{ height }}
                  />
                  <Label className={`${s.current ? 'font-bold' : 'font-medium'} ${tone}`}>{s.label}</Label>
                </div>
              );
            })}
          </div>
        );
      }

      case 'HBarRows': {
        const rows = resolveList(comp.rows, dataModel);
        const pct = hbarPercents(rows);
        return (
          <div key={comp.id} className="flex flex-col gap-[6px]">
            {rows.map((r, i) => {
              const back = r.half === 'back';
              return (
                <div key={i} className="flex items-center gap-2">
                  <Label className="w-[14px] text-right font-medium text-rr-subtle">{r.km}</Label>
                  <p
                    className={`w-10 shrink-0 text-[12px] font-bold leading-4 tracking-[-0.1px] ${back ? 'text-rr-slow' : 'text-rr-primary'}`}
                  >
                    {r.pace_display}
                  </p>
                  <div className="h-2 min-w-0 flex-1 overflow-hidden rounded">
                    <div
                      className={`h-2 rounded ${back ? 'bg-rr-slow' : 'bg-rr-primary'}`}
                      style={{ width: `${pct[i]}%` }}
                    />
                  </div>
                  <Label className="w-[26px] text-right text-rr-subtle">{r.hr ?? ''}</Label>
                </div>
              );
            })}
          </div>
        );
      }

      case 'ElevBars': {
        const rows = resolveList(comp.rows, dataModel);
        const maxAbs = Math.max(0, ...rows.map((r) => Math.abs(Number(r.elev) || 0)));
        return (
          <div key={comp.id} className="flex flex-col gap-[6px]">
            <div className="flex items-end gap-1">
              {rows.map((r, i) => {
                const bar = elevBar(Number(r.elev) || 0, maxAbs);
                const barEl = (
                  <div
                    className={`w-full rounded-[3px] ${bar.up ? 'bg-rr-primary' : 'bg-rr-elev-down'}`}
                    style={{ height: bar.height }}
                  />
                );
                return (
                  <div key={i} className="flex min-w-0 flex-1 flex-col items-center">
                    <p
                      className={`h-3 text-[10px] font-medium leading-3 tracking-[-0.1px] ${bar.up ? 'text-rr-primary' : 'text-rr-subtle'}`}
                    >
                      {bar.label}
                    </p>
                    <div className="flex h-[30px] w-9 max-w-full flex-col justify-end">{bar.up && barEl}</div>
                    <div className="h-px w-full bg-rr-divider" />
                    <div className="flex h-[30px] w-9 max-w-full flex-col">{!bar.up && barEl}</div>
                    <p className="text-[10px] font-medium leading-3 tracking-[-0.1px] text-rr-subtle">{r.km}</p>
                  </div>
                );
              })}
            </div>
            <Label className="font-medium text-rr-subtle">막대 = 구간 고도 변화 (m) · 위 오르막, 아래 내리막</Label>
          </div>
        );
      }

      case 'PlanList': {
        const rows = resolveList(comp.rows, dataModel);
        return (
          <div key={comp.id} className="flex flex-col gap-2">
            {rows.map((r, i) => {
              const muted = r.accent === 'muted';
              return (
                <div
                  key={i}
                  className={`flex items-center gap-3 rounded-xl border-l-[3px] bg-rr-plan-bg px-3 py-[10px] ${
                    muted ? 'border-rr-subtle opacity-75' : 'border-rr-primary'
                  }`}
                >
                  <div className="flex w-[38px] shrink-0 flex-col items-center gap-px whitespace-nowrap">
                    <p
                      className={`text-[12px] font-bold leading-4 tracking-[-0.1px] ${muted ? 'text-rr-subtle' : 'text-rr-primary'}`}
                    >
                      {r.day}
                    </p>
                    <p className="text-[10px] leading-3 tracking-[-0.3px] text-rr-subtle">{r.date}</p>
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-px tracking-[-0.3px]">
                    <p className="text-[13px] font-bold leading-[18px] text-rr-text">{r.title}</p>
                    {r.detail && <p className="text-[11px] leading-[14px] text-rr-text-tertiary">{r.detail}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      case 'LoadGauge': {
        const level = Number(resolveValue(comp.level, dataModel));
        if (!Number.isFinite(level)) return null;
        const { ratio, zone } = gaugePosition(level, Number(comp.max) || 5);
        return (
          <div key={comp.id} className="flex flex-col gap-2">
            <div className="relative flex items-center opacity-[0.92]">
              <div className="h-[10px] flex-1 rounded-l-[5px] bg-rr-gauge-low" />
              <div className="h-[10px] flex-1 bg-rr-good" />
              <div className="h-[10px] flex-1 rounded-r-[5px] bg-rr-gauge-high" />
              <div className="absolute top-[-5px] size-5 -translate-x-1/2" style={{ left: `${ratio * 100}%` }}>
                <img
                  src={gaugeKnob}
                  alt=""
                  width={32}
                  height={32}
                  className="absolute left-[-6px] top-[-4px] block max-w-none"
                />
              </div>
            </div>
            <div className="flex justify-between whitespace-nowrap text-center text-[10.5px]">
              {GAUGE_LABELS.map((label, i) => (
                <p
                  key={label}
                  className={GAUGE_ZONES[i] === zone ? `font-black ${GAUGE_ZONE_TEXT[zone]}` : 'text-rr-text-tertiary'}
                >
                  {label}
                </p>
              ))}
            </div>
          </div>
        );
      }

      case 'StatTiles':
        return (
          <div key={comp.id} className="flex items-stretch gap-2">
            {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any -- tile shape is schema-driven
              ((comp.tiles as any[] | undefined) ?? []).map((tile, i) => {
                const sub = text(tile.sub);
                return (
                  <div
                    key={i}
                    className="flex min-w-0 flex-1 flex-col gap-[3px] rounded-[13px] bg-rr-tile px-[10px] py-[11px]"
                  >
                    <p className="text-[12px] leading-4 tracking-[-0.3px] text-rr-subtle">{tile.label}</p>
                    <p className="text-[14px] font-bold leading-5 tracking-[-0.1px] text-rr-text">
                      {text(tile.value) || '—'}
                    </p>
                    {sub && (
                      <p
                        className={`text-[11px] font-medium leading-[14px] tracking-[-0.1px] ${SUB_TONES[tile.subTone] ?? SUB_TONES.neutral}`}
                      >
                        {sub}
                      </p>
                    )}
                  </div>
                );
              })
            }
          </div>
        );

      case 'InsightList':
        return (
          <div key={comp.id} className="flex flex-col gap-[6px]">
            {((comp.items as string[] | undefined) ?? []).map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <img src={insightDot} alt="" width={5} height={5} className="mt-[6.5px] block shrink-0" />
                <p className="min-w-0 flex-1 text-[13px] leading-[18px] tracking-[-0.3px] text-rr-text-secondary">
                  {item}
                </p>
              </div>
            ))}
          </div>
        );

      case 'Button': {
        const ghost = comp.style === 'ghost';
        return (
          <button
            key={comp.id}
            type="button"
            disabled={disabled}
            onClick={() => tap(comp, comp.label)}
            className={`flex h-11 w-full items-center justify-center rounded-xl text-[14px] font-bold leading-5 tracking-[-0.1px] disabled:cursor-default ${
              ghost ? 'border-[1.5px] border-rr-primary bg-white text-rr-primary' : 'bg-rr-primary text-white'
            }`}
          >
            {comp.label}
          </button>
        );
      }

      case 'ChipRow':
        return (
          <div key={comp.id} className="flex flex-col items-end gap-[7px]">
            {children(comp).map(render)}
          </div>
        );

      case 'Chip':
        return (
          <button
            key={comp.id}
            type="button"
            disabled={disabled}
            onClick={() => tap(comp, comp.text)}
            className={`rounded-full border-[1.5px] border-rr-chip-border px-4 py-[10px] text-[13px] font-bold leading-[18px] tracking-[-0.1px] text-rr-primary transition-colors enabled:active:bg-rr-chip-pressed disabled:cursor-default ${
              tappedId === comp.id ? 'bg-rr-chip-pressed' : 'bg-white enabled:hover:bg-rr-lightblue-bg'
            } ${disabled && tappedId !== comp.id ? 'opacity-60' : ''}`}
          >
            {comp.text}
          </button>
        );

      case 'Notice': {
        const info = comp.tone === 'info';
        return (
          <div
            key={comp.id}
            className={`flex items-center gap-2 rounded-xl px-3 py-[10px] tracking-[-0.3px] ${info ? 'bg-rr-lightblue-bg' : 'bg-rr-caution-bg'}`}
          >
            <p className="text-[16px] leading-5">{info ? 'ℹ️' : '⚠️'}</p>
            <p
              className={`min-w-0 flex-1 text-[12px] leading-4 ${info ? 'text-rr-text-secondary' : 'text-rr-caution-text'}`}
            >
              {comp.text}
            </p>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Root = the component nobody lists as a child (the server always sends `root` Column).
  const referenced = new Set(components.flatMap((c) => (c.children as string[] | undefined) ?? []));
  const roots = components.filter((c) => !referenced.has(c.id));

  return (
    <div className="w-full max-w-[393px] overflow-hidden rounded-[14px] bg-gradient-to-b from-rr-surface-top via-rr-surface-bottom via-25% to-rr-surface-bottom px-4 pb-6 pt-5 text-left">
      <div className="flex flex-col gap-[14px]">{roots.map(render)}</div>
    </div>
  );
});
RunReportSurface.displayName = 'RunReportSurface';
